import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { WeaponData, createWeapon, updateWeapon } from '../service/weaponService';
import toast from 'react-hot-toast';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

interface WeaponModalProps {
  isOpen: boolean;
  onClose: () => void;
  weapon: WeaponData | null;
  companyId: string;
  onSave: () => void;
}

const WeaponModal: React.FC<WeaponModalProps> = ({
  isOpen,
  onClose,
  weapon,
  companyId,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    weaponId: '',
    weaponType: '',
    serialNumber: '',
    manufacturer: '',
    model: '',
    caliber: '',
    purchaseDate: '',
    purchasePrice: '',
    condition: 'good' as 'excellent' | 'good' | 'fair' | 'poor',
    status: 'available' as 'available' | 'deployed' | 'maintenance' | 'retired',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const formatDateForInput = (date: any): string => {
    if (!date) return '';
    
    try {
      let dateObj: Date;
      
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      // Handle Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Handle string or number
      else if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      }
      // Handle object with seconds/nanoseconds (Firestore Timestamp structure)
      else if (date && typeof date === 'object' && (date.seconds || date._seconds)) {
        const seconds = date.seconds || date._seconds || 0;
        const nanoseconds = date.nanoseconds || date._nanoseconds || 0;
        dateObj = new Date(seconds * 1000 + nanoseconds / 1000000);
      }
      else {
        return '';
      }
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      
      return dateObj.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  useEffect(() => {
    if (weapon) {
      setFormData({
        weaponId: weapon.weaponId || '',
        weaponType: weapon.weaponType || '',
        serialNumber: weapon.serialNumber || '',
        manufacturer: weapon.manufacturer || '',
        model: weapon.model || '',
        caliber: weapon.caliber || '',
        purchaseDate: formatDateForInput(weapon.purchaseDate),
        purchasePrice: weapon.purchasePrice?.toString() || '',
        condition: weapon.condition || 'good',
        status: weapon.status || 'available',
        notes: weapon.notes || '',
      });
    } else {
      setFormData({
        weaponId: '',
        weaponType: '',
        serialNumber: '',
        manufacturer: '',
        model: '',
        caliber: '',
        purchaseDate: '',
        purchasePrice: '',
        condition: 'good',
        status: 'available',
        notes: '',
      });
    }
  }, [weapon, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const weaponData: Omit<WeaponData, 'id' | 'createdAt' | 'updatedAt'> = {
        weaponId: formData.weaponId,
        weaponType: formData.weaponType,
        serialNumber: formData.serialNumber,
        manufacturer: formData.manufacturer || undefined,
        model: formData.model || undefined,
        caliber: formData.caliber || undefined,
        purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate) : undefined,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
        condition: formData.condition,
        status: formData.status,
        notes: formData.notes || undefined,
      };

      if (weapon) {
        await updateWeapon(weapon.id, weaponData, companyId);
        toast.success('Weapon updated successfully');
      } else {
        await createWeapon(weaponData, companyId);
        toast.success('Weapon created successfully');
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving weapon:', error);
      toast.error(error.message || 'Failed to save weapon');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={weapon ? 'Edit Weapon' : 'Add New Weapon'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              const form = document.getElementById('weapon-form') as HTMLFormElement;
              if (form) {
                form.requestSubmit();
              }
            }} 
            loading={saving} 
            disabled={saving}
          >
            {weapon ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="weapon-form" onSubmit={handleSubmit}>
        <ModalContent>
          <FormRow>
            <FormGroup>
              <Label>Weapon ID *</Label>
              <Input
                value={formData.weaponId}
                onChange={(e) => setFormData({ ...formData, weaponId: e.target.value })}
                placeholder="e.g., WPN-001"
                required
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Weapon Type *</Label>
              <Input
                value={formData.weaponType}
                onChange={(e) => setFormData({ ...formData, weaponType: e.target.value })}
                placeholder="e.g., Pistol, Rifle, Shotgun"
                required
                dark
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Serial Number *</Label>
              <Input
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                placeholder="Serial number"
                required
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Manufacturer</Label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="Manufacturer name"
                dark
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Model</Label>
              <Input
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="Model name"
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Caliber</Label>
              <Input
                value={formData.caliber}
                onChange={(e) => setFormData({ ...formData, caliber: e.target.value })}
                placeholder="e.g., 9mm, .45 ACP"
                dark
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Purchase Date</Label>
              <Input
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Purchase Price</Label>
              <Input
                type="text"
                value={formData.purchasePrice}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers and decimal point
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setFormData({ ...formData, purchasePrice: value });
                  }
                }}
                placeholder="0.00"
                dark
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Condition *</Label>
              <Select
                value={formData.condition}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    condition: e.target.value as 'excellent' | 'good' | 'fair' | 'poor',
                  })
                }
                required
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Status *</Label>
              <Select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as 'available' | 'deployed' | 'maintenance' | 'retired',
                  })
                }
                required
              >
                <option value="available">Available</option>
                <option value="deployed">Deployed</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Notes</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              dark
            />
          </FormGroup>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default WeaponModal;

